import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { put } from '@/lib/api';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

interface Sharing {
  withinGroup: boolean;
  acrossPlatform: boolean;
  hasGroup: boolean;
}

export function TenantHistorySharingPanel({
  tenantId,
  slug,
  sharing,
}: {
  tenantId: string;
  slug: string;
  sharing: Sharing;
}) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ field: 'group' | 'platform'; next: boolean } | null>(null);
  const canChange = useOwnerCapability('tenant.sharing.update');

  const rows = [
    {
      field: 'group' as const,
      title: 'Across this facility’s own group',
      on: sharing.withinGroup,
      blurb:
        'A doctor here can open the same patient’s visits at the other branches of this group — ' +
        'one organisation, one record.',
      blocked: !sharing.hasGroup ? 'This facility is not part of a group yet.' : null,
    },
    {
      field: 'platform' as const,
      title: 'Across every other facility on the platform',
      on: sharing.acrossPlatform,
      blurb:
        'A doctor here can open the same patient’s visits at hospitals that are nothing to do with ' +
        'this one. The list never repeats what the group tab already shows.',
      blocked: null,
    },
  ];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Patient history sharing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            Both are off unless you turn them on. They decide whether this facility’s doctors can read a
            patient’s record from somewhere else — so turn one on only once the approvals behind it are in
            place. Every read is written to the audit trail with who read it and from how many facilities.
          </Alert>

          {rows.map((row) => (
            <div key={row.field} className="flex flex-wrap items-start justify-between gap-3 rounded-md border p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-medium">{row.title}</p>
                  <Badge variant={row.on ? 'success' : 'muted'}>{row.on ? 'On' : 'Off'}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{row.blurb}</p>
                {row.blocked && <p className="mt-1 text-xs text-muted-foreground">{row.blocked}</p>}
              </div>
              {canChange && (
                <Button
                  variant={row.on ? 'outline' : 'default'}
                  size="sm"
                  disabled={Boolean(row.blocked) && !row.on}
                  onClick={() => setPending({ field: row.field, next: !row.on })}
                >
                  {row.on ? 'Turn off' : 'Turn on'}
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {pending && (
        <OwnerConfirmDialog
          open
          onClose={() => setPending(null)}
          title={
            pending.next
              ? pending.field === 'group'
                ? 'Share patient history across the group'
                : 'Share patient history across the platform'
              : 'Stop sharing patient history'
          }
          description={slug}
          confirmLabel={pending.next ? 'Turn it on' : 'Turn it off'}
          successMessage={pending.next ? 'Sharing is on' : 'Sharing is off'}
          destructive={pending.next && pending.field === 'platform'}
          warning={
            pending.next && pending.field === 'platform' ? (
              <>
                This lets doctors here read a patient’s clinical history recorded at hospitals that are
                nothing to do with this one. Confirm the patient consent and the agreements behind it are
                in place before you turn it on.
              </>
            ) : undefined
          }
          action={(payload) =>
            put(`/platform/tenants/${tenantId}/sharing`, {
              ...payload,
              ...(pending.field === 'group'
                ? { shareHistoryWithinGroup: pending.next }
                : { shareHistoryAcrossPlatform: pending.next }),
            })
          }
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            setPending(null);
          }}
        />
      )}
    </>
  );
}
